import React, { useState, useEffect, useRef } from 'react';
import { restLabel } from '../phaseGrid';

// The rest between sets.
//
// # WHY THIS IS ITS OWN COMPONENT
//
// It ticks once a second. Kept inside the logger, every tick would re-render the
// whole screen — including the inputs an athlete is typing a load into, which is
// how a keystroke gets dropped mid-set.
//
// # WHY IT COUNTS UP PAST ZERO
//
// It does not stop at zero and it does not nag. A prescribed 120s is a floor,
// not a deadline; an athlete who needs 140 has not done anything wrong, and a
// timer that hides itself at zero takes away the only way to know they took 140.

//
// # WHY IT CAN START IN THE PAST
//
// `startedAt` is when the rest actually began: the moment Log set was pressed,
// not the moment the save came back and this mounted. On a slow connection the
// athlete is already resting while the POST is in flight; counting from mount
// showed a full 10s countdown after an 8s save that had already used 8 of them.
// Omitted, the rest starts now.

const secondsSince = (startedAt) => Math.max(Math.floor((Date.now() - startedAt) / 1000), 0);

const RestTimer = ({ seconds, role, nextLabel, onSkip, startedAt: restStartedAt }) => {
  const startedAt = useRef(restStartedAt ?? Date.now());
  const [elapsed, setElapsed] = useState(() => secondsSince(startedAt.current));

  useEffect(() => {
    startedAt.current = restStartedAt ?? Date.now();
    setElapsed(secondsSince(startedAt.current));

    // Derived from a timestamp rather than incremented, because a phone locks
    // the screen mid-rest and throttles the interval — counting ticks would
    // report 40 seconds after a real 120.
    const id = setInterval(() => {
      setElapsed(secondsSince(startedAt.current));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds, nextLabel, restStartedAt]);

  const target = Number(seconds) || 0;
  const remaining = target - elapsed;
  const over = remaining < 0;

  return (
    <div className={`rest-timer${over ? ' rest-timer-over' : ''}`} role="timer" aria-live="off">
      <div className="rest-timer-count mono">
        {over ? `+${formatClock(-remaining)}` : formatClock(Math.max(remaining, 0))}
      </div>
      <div className="rest-timer-role">
        {restLabel(target, role)}
        {over && <span className="rest-timer-over-note"> · over</span>}
      </div>
      <div className="rest-timer-next">
        Next: <strong>{nextLabel}</strong>
      </div>
      <button type="button" className="btn-primary rest-timer-skip" onClick={onSkip}>
        {over ? 'Continue' : 'Skip rest'}
      </button>
    </div>
  );
};

/** m:ss, because 90 seconds reads as 1:30 to anyone standing under a bar. */
export function formatClock(totalSeconds) {
  const value = Math.max(Math.floor(Number(totalSeconds) || 0), 0);
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default RestTimer;
